import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Header global-search modal — Owner / Head / PM only.
 * Typeahead across sites, users, staff, vendors, assets, SRs, documents.
 * Cmd/Ctrl-K opens; Escape closes; arrow keys navigate results.
 */
export default function GlobalSearch({ open, onClose }) {
    const { user } = useAuth();
    const eff = user?.effective_role || user?.role;
    const enabled = eff === "OWNER" || eff === "HEAD" || eff === "PROJECT_MANAGER";
    const [q, setQ] = useState("");
    const [hits, setHits] = useState([]);
    const [busy, setBusy] = useState(false);
    const [activeIdx, setActiveIdx] = useState(0);
    const inputRef = useRef(null);
    const nav = useNavigate();

    useEffect(() => {
        if (open && inputRef.current) { setTimeout(() => inputRef.current?.focus(), 40); }
        if (!open) { setQ(""); setHits([]); setActiveIdx(0); }
    }, [open]);

    useEffect(() => {
        if (!q || q.length < 2) { setHits([]); return; }
        setBusy(true);
        const h = setTimeout(async () => {
            try {
                const r = await api.get(`/search?q=${encodeURIComponent(q)}`);
                setHits(r.data.hits || []); setActiveIdx(0);
            } catch { setHits([]); }
            finally { setBusy(false); }
        }, 200);
        return () => clearTimeout(h);
    }, [q]);

    function keyDown(e) {
        if (e.key === "Escape") { onClose(); }
        else if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(hits.length - 1, i + 1)); }
        else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(0, i - 1)); }
        else if (e.key === "Enter" && hits[activeIdx]) { nav(hits[activeIdx].to); onClose(); }
    }

    if (!open || !enabled) return null;
    return (
        <div className="fixed inset-0 z-[60] bg-black/50" onClick={onClose} data-testid="global-search-backdrop">
            <div
                className="absolute left-1/2 -translate-x-1/2 top-16 w-full max-w-lg bg-white rounded-xl shadow-2xl"
                onClick={(e) => e.stopPropagation()}
                data-testid="global-search"
            >
                <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200">
                    <Search className="w-4 h-4 text-gray-400" />
                    <input
                        ref={inputRef}
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={keyDown}
                        placeholder="Search sites, staff, vendors, assets, SRs, documents…"
                        className="flex-1 outline-none text-sm py-1.5"
                        data-testid="global-search-input"
                    />
                    <button onClick={onClose} className="p-1 rounded hover:bg-gray-100" data-testid="global-search-close" aria-label="Close">
                        <X className="w-4 h-4 text-gray-500" />
                    </button>
                </div>
                <div className="max-h-[60vh] overflow-y-auto">
                    {busy && <div className="p-6 text-center text-xs text-gray-500">Searching…</div>}
                    {!busy && q.length < 2 && <div className="p-6 text-center text-xs text-gray-500">Type 2+ characters to search.</div>}
                    {!busy && q.length >= 2 && hits.length === 0 && <div className="p-6 text-center text-xs text-gray-500" data-testid="global-search-empty">No results.</div>}
                    <ul>
                        {hits.map((h, i) => (
                            <li key={i}>
                                <button
                                    onClick={() => { nav(h.to); onClose(); }}
                                    onMouseEnter={() => setActiveIdx(i)}
                                    className={`w-full text-left flex items-start gap-2 px-3 py-2 border-b border-gray-100 ${activeIdx === i ? "bg-gray-100" : "hover:bg-gray-50"}`}
                                    data-testid={`search-hit-${i}`}
                                >
                                    <span className="text-[10px] uppercase tracking-wider bg-[#1E3355] text-white px-1.5 py-0.5 rounded shrink-0 mt-0.5">{h.kind}</span>
                                    <span className="text-sm text-gray-800 flex-1 truncate">{h.label}</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
                {hits.length > 0 && (
                    <div className="px-3 py-1.5 text-[10px] text-gray-500 border-t border-gray-100">
                        ↑ ↓ navigate · Enter to open · Esc to close
                    </div>
                )}
            </div>
        </div>
    );
}
